import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import uniqueId from 'lodash/uniqueId';

import type { ChatContextValue } from '@/features/ai-chat/state/chat.context';
import { ChatContext } from '@/features/ai-chat/state/chat.context';
import useUserDetails from '@/hooks/useUserDetails';
import { useStorageValue } from '@/services/storage/react';
import { useChatAnalytics } from '../../hooks/useChatAnalytics';
import { useChatHistorySync } from '../../hooks/useChatHistorySync';
import { useInitializeChat } from '../../hooks/useInitializeChat';
import { useSendMessage } from '../../hooks/useSendMessage';
import { useUrlPromptIntegration } from '../../hooks/useUrlPromptIntegration';
import { appendPartToMessages } from '../../lib/messages';
import type {
  AIMessage,
  ContentPart,
  Conversation,
  HumanMessage,
  Message
} from '../../types/chat.types';

export const ChatProvider = ({ open, children }: { open: boolean; children: React.ReactNode }) => {
  const { userEmail } = useUserDetails();
  const tripToken = useStorageValue('auth.tripToken');

  const [conversationId, setConversationId] = useState<string | null>('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [animatedHumanId, setAnimatedHumanId] = useState<string | null>(null);
  const [animatedText, setAnimatedText] = useState<string | null>(null);
  const [inputMessage, setInputMessage] = useState('');
  const [version, setVersion] = useState(0);
  const messagesRef = useRef<Message[]>([]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const initChat = useInitializeChat();

  const saveMessageToHistory = useChatHistorySync(conversationId, messagesRef);
  const sendPosthogEvent = useChatAnalytics(conversationId, messagesRef);

  const appendPart = useCallback((part: ContentPart) => {
    setMessages(prev => {
      const next = appendPartToMessages(prev, part);
      messagesRef.current = next;
      return next;
    });
  }, []);

  const handleSetConversationId = useCallback((nextConversationId: string | null) => {
    if (nextConversationId === '') {
      messagesRef.current = [];
      setMessages([]);
    }
    setConversationId(nextConversationId);
  }, []);

  const { mutation: sendMutation, cancel: cancelSend } = useSendMessage({
    onPart: appendPart,
    onComplete: () => {
      saveMessageToHistory();
      sendPosthogEvent();
    }
  });

  useEffect(() => {
    if (open && conversationId === '' && !initChat.isPending && tripToken) {
      cancelSend();
      sendMutation.reset();
      initChat.mutate(undefined, {
        onSuccess: data => {
          setConversationId(data.conversation_id);
          setVersion(data.version ? Number(data.version) : 0);
        },
        onError: () => {
          setConversationId(null);
        }
      });
    }
  }, [open, conversationId, initChat, sendMutation, cancelSend, tripToken]);

  const sendMessage = useCallback(
    (text: string, options?: { addHuman?: boolean }) => {
      if (!conversationId || !text.trim()) return;

      setMessages(prev => {
        const now = new Date().toISOString();
        const human: HumanMessage | null =
          options?.addHuman === false
            ? null
            : { id: uniqueId(now), sender: 'human', text, timestamp: now };
        const ai: AIMessage = { id: uniqueId(now), sender: 'ai', timestamp: now, content: [] };
        const next: Message[] = human ? [...prev, human, ai] : [...prev, ai];
        messagesRef.current = next;
        return next;
      });

      sendMutation.mutate({ conversationId, message: text, userId: userEmail || '' });
    },
    [conversationId, userEmail, sendMutation]
  );

  const handleSend = useCallback(() => {
    if (!inputMessage.trim() || animatedText) return;
    sendMessage(inputMessage);
    setInputMessage('');
  }, [inputMessage, sendMessage, animatedText]);

  useEffect(() => {
    if (conversationId) cancelSend();
  }, [conversationId, cancelSend]);

  useUrlPromptIntegration({
    conversationId,
    messagesRef,
    setMessages,
    setAnimatedHumanId,
    setAnimatedText
  });

  const onAnimatedComplete = useCallback(() => {
    if (!animatedText) return;
    sendMessage(animatedText, { addHuman: false });
    setAnimatedHumanId(null);
    setAnimatedText(null);
  }, [animatedText, sendMessage]);

  const restoreConversation = useCallback((conversation: Conversation) => {
    messagesRef.current = conversation.messages;
    setMessages(conversation.messages);
    setConversationId(conversation.conversationId);
  }, []);

  const value = useMemo<ChatContextValue>(
    () => ({
      messages,
      setMessages,
      restoreConversation,
      inputMessage,
      setInputMessage,
      handleSend,
      sendMessage,
      isInitializing: initChat.isPending,
      isSending: sendMutation.isPending,
      setConversationId: handleSetConversationId,
      conversationId,
      version,
      animatedHumanId,
      animatedText,
      onAnimatedComplete
    }),
    [
      messages,
      restoreConversation,
      inputMessage,
      handleSend,
      sendMessage,
      initChat.isPending,
      sendMutation.isPending,
      handleSetConversationId,
      conversationId,
      version,
      animatedHumanId,
      animatedText,
      onAnimatedComplete
    ]
  );

  return <ChatContext value={value}>{children}</ChatContext>;
};
